import { Form } from '@form/Form';
import { FormDivider } from '@form/FormDivider';
import FormInput from '@form/FormInput';
import { Blockquote } from '@ui';
import React, { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { pushAlert } from 'redux/actions/alertActions';
import { logout } from 'redux/actions/authActions';
import { useApi } from '../../hooks/useApi';
import { flushImages } from '../../redux/actions/cacheActions';

interface Props {
    /** Called after a successful save so the caller can refresh its user data. */
    cb: () => void;
    user: any;
}

// Checkbox for the incognito flag; registers directly against the surrounding form context.
const PrivateModeField = () => {
    const { register } = useFormContext();

    return (
        <div className="tw-mt-auto">
            <label className="tw-flex tw-items-center tw-gap-2 tw-py-4">
                <input
                    type="checkbox"
                    className="tw-size-4 tw-rounded tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-text-success focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-success"
                    {...register('private')}
                />
                <span className="tw-text-sm tw-font-medium tw-text-accents-9">Inkognito Modus</span>
            </label>
            <Blockquote className="!tw-mb-6 !tw-mt-0">
                Name und Profil werden nicht öffentlich in Buchungsübersichten oder Ähnlichem gezeigt.
            </Blockquote>
        </div>
    );
};

export const AccountSettingsForm: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
    cb,
    user
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    const [images, setImages] = useState(user?.image ? [user?.image] : []);
    const api = useApi();

    // Memoized so the object identity only changes when the user data actually changes.
    // The shared Form re-seeds every field from defaultValues whenever this reference is
    // new (setDefaultValuesOnRerender defaults to true) — an inline object would do that on
    // every single render and throw away what the user just typed.
    const defaultValues = useMemo(
        () => ({
            ...user,
            private: user.private === 1,
            address: {
                street: user.street,
                streetnumber: user.streetnumber,
                city: user.city,
                postalcode: user.postcode
            }
        }),
        [user]
    );

    // Saves the profile fields, then lets the caller refresh its user data.
    const handleSubmitGeneral = (data) => {
        setIsLoading(true);
        return api({
            url: 'user/settings',
            method: 'POST',
            data,
            onSuccess: () => cb?.(),
            onBefore: () => setIsLoading(false)
        });
    };

    // Changes the password; no user payload comes back, the caller just revalidates.
    const handleSubmitPasswort = (data) => {
        setIsLoading(true);
        return api({
            url: 'user/settings/changepassword',
            method: 'POST',
            data,
            onSuccess: () => cb?.(),
            onBefore: () => setIsLoading(false)
        });
    };

    // Deletes the account, flushes cached images and logs the user out.
    const handleSubmitDelete = (data) => {
        setIsLoading(true);
        void api({
            url: 'user',
            method: 'DELETE',
            data: { ...data, images },
            onBefore: () => setIsLoading(false),
            onSuccess: () => {
                dispatch(flushImages(images));
                dispatch(pushAlert('Dein Account wurde gelöscht!', 'success'));
                dispatch(logout());
            }
        });
    };

    return (
        <>
            <Form onSubmit={handleSubmitGeneral} defaultValues={defaultValues}>
                <div className="tw-grid md:tw-grid-cols-2 md:tw-gap-x-6">
                    <FormInput name="name" label="Name" />
                    <FormInput name="email" label="E-Mail" />
                    <FormInput name="phone" label="Telefonnummer" />
                    <FormInput.DatePicker name="date_of_birth" label="Geburtsdatum" />
                    <FormInput.IBAN name="iban" label="IBAN" />
                    <FormInput name="invoice_recipient" label="Rechnungsempfänger" />
                    <FormInput.Address name="address" label="Adresse" />
                    <PrivateModeField />
                </div>
                <FormInput.WYSIWYG hideTemplates name="about" label="Über mich" />

                <FormInput.File
                    name="image"
                    label="Profilbild"
                    image
                    aspectRatio={1}
                    cropperOptions={{
                        width: 1000,
                        height: 1000,
                        minWidth: 256,
                        minHeight: 256,
                        maxWidth: 4096,
                        maxHeight: 4096,
                        fillColor: '#fff',
                        imageSmoothingEnabled: true,
                        imageSmoothingQuality: 'high'
                    }}
                />
                <FormInput.Submit isLoading={isLoading} />
            </Form>

            <FormDivider title="Passwort ändern" collapsible>
                <Form onSubmit={handleSubmitPasswort}>
                    <FormInput type="password" name="password_current" label="Aktuelles Passwort" />
                    <FormInput type="password" name="password" label="Neues Passwort" />
                    <FormInput type="password" name="password_confirmation" label="Neues Passwort bestätigen" />
                    <FormInput.Submit color="light" isLoading={isLoading}>
                        Passwort ändern
                    </FormInput.Submit>
                </Form>
            </FormDivider>

            <FormDivider title="Account löschen" collapsible>
                <Form onSubmit={handleSubmitDelete}>
                    <FormInput type="password" name="password" label="Passwort" />
                    <FormInput.Submit isLoading={isLoading} color="error">
                        Account löschen
                    </FormInput.Submit>
                </Form>
            </FormDivider>
        </>
    );
};
